import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listNotifications, markNotificationsRead, dismissNotification } from "@/lib/journey.functions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/journey/notifications")({
  component: NotificationsPage,
});

type N = Awaited<ReturnType<typeof listNotifications>>[number];

function NotificationsPage() {
  const load = useServerFn(listNotifications);
  const mark = useServerFn(markNotificationsRead);
  const dismiss = useServerFn(dismissNotification);
  const [items, setItems] = useState<N[]>([]);
  const [filter, setFilter] = useState<string>("");

  const refresh = () => load({ data: { category: filter || undefined } }).then(setItems).catch(() => {});
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const categories = Array.from(new Set(items.map((i) => i.category)));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter by category"
          className="rounded-md border hairline bg-obsidian px-2 py-2 text-sm"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button variant="outline" size="sm" onClick={async () => { await mark({ data: { all: true } }); toast.success("All marked read"); refresh(); }}>
          <Check size={14} className="mr-1" />Mark all read
        </Button>
      </div>
      {items.length === 0 && <p className="text-sm text-foreground/60">No notifications.</p>}
      <ul className="space-y-2">
        {items.map((n) => (
          <li key={n.id}>
            <Card className={`flex items-start justify-between gap-3 p-4 ${!n.read_at ? "border-gold/50" : ""}`}>
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/50">
                  <span>{n.category}</span>
                  {!n.read_at && <span className="rounded-full bg-gold/20 px-2 py-0.5 text-gold">new</span>}
                </div>
                <div className="mt-1 font-medium">{n.title}</div>
                {n.body && <div className="mt-1 text-sm text-foreground/70">{n.body}</div>}
                <div className="mt-1 text-xs text-foreground/50">{new Date(n.created_at).toLocaleString()}</div>
                {n.link && <a href={n.link} className="mt-2 inline-block text-sm text-gold underline">Open →</a>}
              </div>
              <div className="flex items-center gap-1">
                {!n.read_at && (
                  <Button variant="ghost" size="icon" aria-label="Mark read" onClick={async () => { await mark({ data: { ids: [n.id] } }); refresh(); }}>
                    <Check size={14} />
                  </Button>
                )}
                <Button variant="ghost" size="icon" aria-label="Dismiss" onClick={async () => { await dismiss({ data: { id: n.id } }); refresh(); }}>
                  <X size={14} />
                </Button>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}
